import Phaser from 'phaser';
import {
  StateMachine,
  gridMove,
  frameMs,
  relativeDirection,
  oppositeDirection,
  weightedChoice,
  withinFacingCone,
} from 'tower/util';
import BattleScene from 'tower/scenes/battle';
import Enemy, { EnemyOptions, EnemyState } from 'tower/scenes/battle/enemy';
import { HitOptions } from 'tower/types';
import * as C from 'tower/constants';
import gameState from 'tower/state/game-state';

const MOVE_SPEED = 30;

interface KnightOptions extends EnemyOptions {
  blockedSpaces?: { x: number; y: number }[];
}

interface Variant {
  idle: number;
  hp: number;
  touchDamage: number;
  blocking: boolean;
}

const VARIANTS: Record<string, Variant> = {
  green: {
    idle: 64,
    hp: 4,
    touchDamage: 1,
    blocking: false,
  },
  red: {
    idle: 32,
    hp: 4,
    touchDamage: 1,
    blocking: true,
  },
  blue: {
    idle: 96,
    hp: 5,
    touchDamage: 2,
    blocking: true,
  },
  dark: {
    idle: 288,
    hp: 5,
    touchDamage: 1,
    blocking: false,
  },
};

export default class Knight extends Enemy {
  constructor(
    scene: BattleScene,
    x: number,
    y: number,
    public variantType = 'red',
    public knightOptions: KnightOptions = {},
  ) {
    const variant = VARIANTS[variantType];
    super(scene, x, y, 'enemies', variant.idle, 'knight', {
      hp: variant.hp,
      touchDamage: variant.touchDamage,
      knockback: false,
    });
    this.body.setSize(12, 12);
  }

  makeStateMachine(scene: BattleScene): StateMachine<EnemyState> {
    return new StateMachine('moving', {
      moving: new KnightMovingState(scene, this),
    });
  }

  get variant() {
    return VARIANTS[this.variantType];
  }

  async hit(options: HitOptions) {
    const { weaponType } = options;

    // Invulnerable to boomerangs and arrows
    if (weaponType === 'boomerang') {
      this.scene.sound.play('deflectSmall');
      return;
    }

    // Some knights deflect attacks from the front
    if (this.variant.blocking) {
      // Held weapon directions can be finnicky given how close they are, so we use the player's position for them instead.
      if (weaponType === 'sword') {
        if (
          gameState.sword !== 'masterSword' &&
          withinFacingCone(this, this.scene.player, this.direction, C.KNIGHT_SHIELD_CONE_SIZE)
        ) {
          this.scene.sound.play('deflectBig');
          return;
        }
      } else if (withinFacingCone(this, options.weaponBody.prevFrame, this.direction, C.KNIGHT_SHIELD_CONE_SIZE)) {
        this.scene.sound.play(weaponType === 'sword' ? 'deflectBig' : 'deflectSmall');
        return;
      }
    }

    return super.hit(options);
  }

  static create(scene: BattleScene) {
    for (const variant of Object.keys(VARIANTS)) {
      const idle = VARIANTS[variant].idle;
      scene.anims.create({
        key: `knight[${variant}][down].move`,
        duration: frameMs(28),
        frames: scene.anims.generateFrameNumbers('enemies', {
          frames: [idle, idle + 1],
        }),
        repeat: -1,
      });
      scene.anims.create({
        key: `knight[${variant}][up].move`,
        duration: frameMs(28),
        frames: scene.anims.generateFrameNumbers('enemies', {
          frames: [idle + 2, idle + 3],
        }),
        repeat: -1,
      });
      scene.anims.create({
        key: `knight[${variant}][left].move`,
        duration: frameMs(28),
        frames: scene.anims.generateFrameNumbers('enemies', {
          frames: [idle + 4, idle + 5],
        }),
        repeat: -1,
      });
      scene.anims.create({
        key: `knight[${variant}][right].move`,
        duration: frameMs(28),
        frames: scene.anims.generateFrameNumbers('enemies', {
          frames: [idle + 4, idle + 5],
        }),
        repeat: -1,
      });
    }
  }
}

class KnightState extends EnemyState {
  declare enemy: Knight;
}

class KnightMovingState extends KnightState {
  targetX!: number;
  targetY!: number;
  blockedFromPlayer = 0;
  reached = true;

  async handleEntered() {
    const { enemy, scene } = this;

    // If we did not reach our previous target, we got interrupted (e.g. hit) and should keep going.
    if (!this.reached) {
      return;
    }
    this.reached = false;

    const maze = scene.currentRoom.maze;
    const [sx, sy] = maze.worldXYToSpaceXY(enemy.x, enemy.y);

    // Filter out blocked spaces
    const openDirections = maze.getOpenDirectionsAtWorldXY(enemy.x, enemy.y).filter((d) => {
      const [nsx, nsy] = gridMove(sx, sy, d);
      if (enemy.knightOptions.blockedSpaces?.some((blocked) => blocked.x === nsx && blocked.y === nsy)) {
        return false;
      }

      return true;
    });

    // Store a cooldown for if a wall separates us and the player
    const playerDirection = relativeDirection(enemy, scene.player);
    if (!openDirections.includes(playerDirection)) {
      this.blockedFromPlayer = 5;
    } else {
      this.blockedFromPlayer--;
    }

    const direction = weightedChoice(
      openDirections.map((d) => {
        // Prefer moving towards the player, if they are close and we aren't on cooldown
        if (
          d === playerDirection &&
          Phaser.Math.Distance.BetweenPoints(enemy, scene.player) < 24 * 4 &&
          this.blockedFromPlayer < 1
        ) {
          return [d, 2];
        }

        // Slightly prefer moving in the same direction
        if (d === enemy.direction) {
          return [d, 1.2];
        }

        // Prefer avoiding the direction we just came from
        if (d === oppositeDirection(enemy.direction)) {
          return [d, 0.3];
        }

        return [d, 1];
      }),
    );

    const [spaceX, spaceY] = maze.worldXYToSpaceXY(enemy.x, enemy.y);
    const [newSpaceX, newSpaceY] = gridMove(spaceX, spaceY, direction, 1);
    const [newWorldX, newWorldY] = maze.spaceXYToWorldXY(newSpaceX, newSpaceY);

    this.targetX = newWorldX;
    this.targetY = newWorldY;

    enemy.direction = direction;
    enemy.play(`knight[${enemy.variantType}][${direction}].move`);
  }

  update() {
    const { enemy, scene } = this;
    scene.physics.moveTo(enemy, this.targetX, this.targetY, MOVE_SPEED);
    if (Phaser.Math.Distance.Between(enemy.x, enemy.y, this.targetX, this.targetY) <= 1) {
      enemy.body.reset(this.targetX, this.targetY);
      this.reached = true;
      return this.transition('moving');
    }

    // If blocked, give up
    if (!enemy.body.blocked.none) {
      enemy.body.stop();
      this.reached = true;
      return this.transition('attack');
    }
  }
}
